import { memo, useCallback, useState } from 'react'

import { ContactTypeItems } from '@/components/Domains/Contact/const/utils'
import { useSendContact } from '@/components/Domains/Contact/hooks/useSendContact'
import { BreadCrumbs } from '@/components/UIParts/Breadcrumbs'
import { Button } from '@/components/UIParts/Button'
import { Checkbox } from '@/components/UIParts/Form/Elements/Checkbox'
import { Label } from '@/components/UIParts/Form/Elements/Label'
import { FormSelect } from '@/components/UIParts/Form/FormSelect'
import { FormTextarea } from '@/components/UIParts/Form/FormTextarea'
import { FormTextfield } from '@/components/UIParts/Form/FormTextfield'
import { Heading } from '@/components/UIParts/Heading'
import { replaceBrText } from '@/hooks/useUtils'
import { pagesPath } from '@/lib/$path'
import { NextLink } from '@/lib/next'

type Phase = 'input' | 'confirm' | 'complete'
export const View = memo(() => {
  const [currentPhase, setPhase] = useState<Phase>('input')
  const onChangePhase = useCallback((phase: Phase) => {
    window.scrollTo(0, 0)
    setPhase(phase)
  }, [])

  const sendContact = useSendContact({
    onSuccess: () => onChangePhase('complete'),
    onError: () => onChangePhase('input'),
  })

  return (
    <div className="pb-36 pt-16 md:pb-[15.875rem] md:pt-[7.375rem]">
      <div className="container">
        <BreadCrumbs
          lists={[
            {
              label: 'お問い合わせ',
            },
          ]}
          className="mb-14 md:mb-20"
        />

        <Heading as="h1" main="Contact" sub="お問い合わせ" className="mb-6 md:mb-10" />

        <form onSubmit={sendContact.onSubmit} className="md:px-40">
          {currentPhase === 'input' && <Form onChangePhase={onChangePhase} {...sendContact} />}
          {currentPhase === 'confirm' && <Confirm onChangePhase={onChangePhase} {...sendContact} />}
          {currentPhase === 'complete' && <Complete />}
        </form>
      </div>
    </div>
  )
})

type FormProps = {
  onChangePhase: (phase: Phase) => void
} & ReturnType<typeof useSendContact>

const Form = ({ form, errorMessage, isDisabled, onChangePhase }: FormProps) => {
  return (
    <>
      <div className="mb-16 text-center text-sm font-bold md:text-base">
        <p className="mb-6 leading-loose">
          この度は弊社のサイトをご覧いただきまして、
          <br className="block md:hidden" />
          ありがとうございます。
          <br />
          依頼のご相談、料金のお見積もり、ご質問など、お気軽にお問い合わせください。
        </p>
        <p className="leading-loose">
          お問い合わせいただいた内容を確認し、
          <br className="md:hidden" />
          担当者よりご連絡させていただきます。
        </p>
      </div>

      {!!errorMessage && (
        <div className="mb-4 bg-red-100 p-4 text-center font-bold">
          <p>{errorMessage}</p>
        </div>
      )}

      <div className="mb-16 grid md:grid-cols-[max-content,minmax(0,1fr)] md:gap-x-10 md:gap-y-8">
        <div className="pt-4">
          <Label isRequired>貴社名</Label>
        </div>
        <FormTextfield
          {...form.register('company_name')}
          placeholder="株式会社ApplyNow"
          errorMessage={form.formState.errors?.company_name?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label>部署名</Label>
        </div>
        <FormTextfield
          {...form.register('department_name')}
          placeholder="入力してください"
          errorMessage={form.formState.errors?.department_name?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label>役職</Label>
        </div>
        <FormTextfield
          {...form.register('position')}
          placeholder="入力してください"
          errorMessage={form.formState.errors?.position?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label>貴社URL</Label>
        </div>
        <FormTextfield
          {...form.register('company_url')}
          placeholder="https://applynow.co.jp/"
          errorMessage={form.formState.errors?.company_url?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label isRequired>氏名</Label>
        </div>
        <FormTextfield
          {...form.register('name')}
          placeholder="氏名"
          errorMessage={form.formState.errors?.name?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label isRequired>氏名（ふりがな）</Label>
        </div>
        <FormTextfield
          {...form.register('name_kana')}
          placeholder="氏名（ふりがな）"
          errorMessage={form.formState.errors?.name_kana?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label isRequired>電話番号</Label>
        </div>
        <FormTextfield
          {...form.register('phone_number')}
          placeholder="電話番号"
          hint="ハイフン無し、半角数字で入力してください 例)0312345678"
          errorMessage={form.formState.errors?.phone_number?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label isRequired>メールアドレス</Label>
        </div>
        <FormTextfield
          {...form.register('email')}
          placeholder="メールアドレス"
          hint="半角で入力してください 例）contact@applynow.co.jp"
          errorMessage={form.formState.errors?.email?.message.toString()}
          className="mb-8 md:mb-0"
        />

        <div className="pt-4">
          <Label isRequired>お問い合わせ種別</Label>
        </div>
        <div>
          <p className="mb-3 text-xs font-bold text-attend">
            以下の項目からお問い合わせの内容を選択してください
          </p>
          <FormSelect
            {...form.register('contact_type')}
            items={ContactTypeItems}
            errorMessage={form.formState.errors?.contact_type?.message.toString()}
            className="mb-8 md:mb-0"
          />
        </div>

        <div className="pt-4">
          <Label isRequired>お問い合わせ内容</Label>
        </div>
        <FormTextarea
          {...form.register('message')}
          placeholder="お問い合わせ内容"
          errorMessage={form.formState.errors?.message?.message.toString()}
        />
      </div>

      <div className="mb-8">
        <div className="text-center font-bold">
          <p className="mb-6">
            個人情報の取り扱いについて同意の上、
            <br className="md:hidden" />
            送信してください。
          </p>
          <p className="mb-4 text-sm">個人情報の取り扱いについて</p>
        </div>

        <div className="mx-auto max-h-80 overflow-scroll bg-bg-gray p-6 md:w-[82.5%] md:p-10">
          <PersonalInformationSection />
        </div>
      </div>

      <div>
        <div className="mb-16 text-center">
          <Checkbox label="上記に同意する" value={1} {...form.register('agree')} />
        </div>
        <div className="mx-auto w-60">
          <Button onClick={() => onChangePhase('confirm')} disabled={isDisabled}>
            送信内容を確認する
          </Button>
        </div>
      </div>
    </>
  )
}

type ConfirmProps = {
  onChangePhase: (phase: Phase) => void
} & ReturnType<typeof useSendContact>

const Confirm = ({ form, isDisabled, onChangePhase }: ConfirmProps) => {
  const values = form.getValues()

  return (
    <>
      <p className="mb-10 text-center text-sm font-bold leading-loose md:mb-24 md:text-base">
        送信内容をご確認の上、
        <br className="md:hidden" />
        「送信する」ボタンを押してください。
      </p>
      <div className="mb-16 grid font-bold leading-loose md:grid-cols-[max-content,minmax(0,1fr)] md:gap-x-10 md:gap-y-8">
        <span>貴社名</span>
        <p className="mb-8 md:mb-0">{values.company_name}</p>

        <span>部署名</span>
        <p className="mb-8 md:mb-0">{values.department_name}</p>

        <span>役職</span>
        <p className="mb-8 md:mb-0">{values.position}</p>

        <span>貴社URL</span>
        <p className="mb-8 md:mb-0">{values.company_url}</p>

        <span>氏名</span>
        <p className="mb-8 md:mb-0">{values.name}</p>

        <span>氏名（ふりがな）</span>
        <p className="mb-8 md:mb-0">{values.name_kana}</p>

        <span>電話番号</span>
        <p className="mb-8 md:mb-0">{values.phone_number}</p>

        <span>メールアドレス</span>
        <p className="mb-8 md:mb-0">{values.email}</p>

        <span>お問い合わせ種別</span>
        <p className="mb-8 md:mb-0">{values.contact_type}</p>

        <span>お問い合わせ内容</span>
        <p
          dangerouslySetInnerHTML={{
            __html: replaceBrText(values.message),
          }}
        />
      </div>
      <div>
        <div className="grid gap-x-10 gap-y-4 md:grid-cols-2">
          <Button type="button" theme="secondary" onClick={() => onChangePhase('input')}>
            入力画面にもどる
          </Button>

          <Button type="submit" disabled={isDisabled}>
            送信する
          </Button>
        </div>
      </div>
    </>
  )
}

const Complete = memo(() => {
  return (
    <div className="md:text-center">
      <p className="mb-6 text-center text-base font-bold">お問い合わせの送信が完了しました</p>
      <p className="mb-16 leading-loose">
        担当者から連絡させていただきますので、いましばらくお待ちください。
        <br />
        3営業日以内に担当者より折り返しご連絡させていただきます。
        <br />
        いただいたメールアドレスにご連絡いたしますが、
        <br className="hidden md:block" />
        1週間たっても返信がなかった場合は
        <br className="hidden md:block" />
        入力いただいたメールアドレスが間違っている可能性がございますので
        <br className="hidden md:block" />
        恐れ入りますが再度ご連絡をお願いいたします。
      </p>
      <div className="mb-16 bg-bg-gray p-6 text-left">
        <p className="mb-3 text-sm font-bold">営業日・休業日</p>
        <dl className="grid grid-cols-[max-content,minmax(0,1fr)] gap-x-2 text-xs font-bold leading-loose text-attend">
          <dt>営業日：</dt>
          <dd>
            月曜日～金曜日
            <br className="md:hidden" />
            （10:00～18:00）
          </dd>
          <dt>休業日：</dt>
          <dd>土曜日・日曜日・祝日</dd>
        </dl>
      </div>
      <div>
        <div className="mx-auto w-60">
          <NextLink href={pagesPath.$url()} passHref>
            <Button as="a" theme="secondary">
              TOPにもどる
            </Button>
          </NextLink>
        </div>
      </div>
    </div>
  )
})

const PersonalInformationSection = memo(() => {
  return (
    <section className="c-policy bg-bg-gray">
      <p className="text-sm mb-10">
        個人情報の取扱いについてご確認ください。<br />
        以下をお読みいただき同意いただければ、［送信する］ボタンを押して、お問い合わせへお進みください。
      </p>
      <ul className="c-policy-list" data-type="primary">
        <li>
          <h3 className="primary-title">会社名、個人情報保護管理者の職名・所属及び連絡先</h3>
          <div className='font-bold'>
            株式会社ＡｐｐｌｙＮｏｗ <br />
            管理本部ディレクター <br />
            0120-389-339</div>
        </li>
        <li>
          <h3 className="primary-title">個人情報の利用目的</h3>
          <div className="mb-8">当社は、個人情報を次の各号の目的で利用します。</div>
          <ul className="c-policy-list mb-8" data-type="secondary">
            <li>
              <div className="secondary-title">登録情報、応募動画及び次号の分析結果の企業等への提供</div>
            </li>
            <li>
              <div className="secondary-title">登録情報、応募動画等のAI（人工知能）等による分析</div>
            </li>
            <li>
              <div className="secondary-title">本サービスに関する問い合わせ対応、その他、本サービスの運営</div>
            </li>
            <li>
              <div className="secondary-title">当社または当社の関連会社または業務提携先の商品、サービスに関する情報提供</div>
            </li>
            <li>
              <div className="secondary-title">登録情報を集計、解析等し、個人が特定できない形式への加工をした統計情報、その他情報の自己利用若しくは第三者への提供</div>
            </li>
          </ul>
        </li>
        <li>
          <h3 className="primary-title">個人情報の第三者提供</h3>
          <ul className="c-policy-list" data-type="secondary">
            <li>
              <div className="secondary-title">前項(1)、(3)及び(4)の目的のために、必要な範囲内で、利用者の個人情報（応募動画における利用者の顔や声等の情報を含みます。）を利用者の求めに応じて企業等に提供します。</div>
            </li>
            <li>
              <div className="secondary-title">前項 (2)の目的のために、必要な範囲内で、利用者の個人情報（応募動画における利用者の顔や声等の情報を含みます。）を外部研究機関、大学に提供します。</div>
            </li>
            <li>
              <div className="secondary-title">前項(5)の目的のために、必要な範囲内で、利用者個人が特定できない形式へ加工をした統計情報、その他情報を第三者へ提供します。</div>
            </li>
          </ul>
        </li>
        <li>
          <h3 className="primary-title">個人情報の取扱いの委託について</h3>
          <div>取得した個人情報の取扱いを委託することがあります。</div>
        </li>
        <li>
          <h3 className="primary-title">会社名、個人情報保護管理者の職名・所属及び連絡先</h3>
          <div className='mb-4'>ご本人からの求めにより、当社が保有する開示対象個人情報の、利用目的の通知、開示、内容の訂正、追加または削除、利用の停止、消去及び提供の停止に応じます。</div>
          <div className='font-bold'>
            お問い合わせ窓口 <br />
            管理本部ディレクター <br />
            0120-389-339
          </div>
        </li>
        <li>
          <h3 className="primary-title">個人情報を入力するにあたっての注意事項</h3>
          <ul className="c-policy-list" data-type="secondary">
            <li>
              <div className="secondary-title">お問い合わせの必須項目は漏れなくご入力ください。</div>
            </li>
            <li>
              <div className="secondary-title">ご意見・ご質問の内容は、できるだけ具体的にお願い致します。</div>
            </li>
            <li>
              <div className="secondary-title">ご回答までにお時間をいただく場合もございます。</div>
            </li>
            <li>
              <div className="secondary-title">土曜・日曜・祝日、年末年始休暇・夏季休暇・GW期間は、翌営業日以降のご対応となる場合がございます。</div>
            </li>
            <li>
              <div className="secondary-title">当社の許可なく回答内容の一部または全体を転用、二次利用することは著作法上認められておりませんのでご注意ください。</div>
            </li>
          </ul>
        </li>
        <li>
          <h3 className="primary-title">Cookieの利用について</h3>
          <div>当社が提供するサービスでは、サービスの向上等のため、Cookieを利用して、お客様の閲覧履歴の収集・記録・分析をしており、Cookieのツール提供者等にお客様の閲覧履歴を提供することがある点、ご了承下さい。詳細については、「ApplyNow プライバシーポリシー（
            <a href="https://biz.applynow.jp/privacy/" target='_blank' rel="noopener noreferrer" className='underline'>https://biz.applynow.jp/privacy/</a>）」をご参照下さい。</div>
        </li>
      </ul>
    </section>
  )
})
